import { useParams } from 'react-router-dom';
import { articleDataMap } from '@/mocks/articleContent';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articleDataMap[slug] : undefined;

  if (!article) {
    return (
      <main className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center px-4">
          <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-3">Article Not Found</h1>
          <p className="text-sm text-foreground-600 mb-5">The article you are looking for does not exist or has been moved.</p>
          <a href="/" className="whitespace-nowrap inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-semibold px-6 py-2.5 rounded-full transition-all duration-200 cursor-pointer text-sm">
            <i className="ri-home-line w-4 h-4 flex items-center justify-center"></i>
            Back to Home
          </a>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background-50">
      {/* Navbar */}
      <nav className="w-full bg-white/80 backdrop-blur-sm border-b border-background-200/70 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 cursor-pointer">
            <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
              <i className="ri-grid-line text-accent-600 w-4 h-4 flex items-center justify-center"></i>
            </div>
            <span className="text-sm font-heading font-semibold text-foreground-900 hidden sm:inline">Superbuy Spreadsheet</span>
          </a>
          <div className="flex items-center gap-3">
            <a href="/" className="whitespace-nowrap text-sm text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer">
              Home
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="whitespace-nowrap inline-flex items-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-white text-xs font-semibold px-4 py-2 rounded-full transition-all duration-200 cursor-pointer"
            >
              <i className="ri-shopping-bag-line w-3.5 h-3.5 flex items-center justify-center"></i>
              Shop Now
            </a>
          </div>
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-4">
        <nav className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
          <a href="/" className="hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">Home</a>
          <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
          <span className="text-foreground-800 font-medium">{article.title.length > 50 ? article.title.slice(0, 50) + '...' : article.title}</span>
        </nav>
      </div>

      <article className="max-w-3xl mx-auto px-4 md:px-6 pb-16">
        <header className="mb-8">
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <span className="text-xs font-semibold text-accent-600 bg-accent-100 rounded-full px-3 py-1 whitespace-nowrap">{article.category}</span>
            <span className="text-xs text-foreground-400">•</span>
            <span className="text-xs text-foreground-500 whitespace-nowrap">Updated {article.updatedDate}</span>
            <span className="text-xs text-foreground-400">•</span>
            <span className="text-xs text-foreground-500 whitespace-nowrap">{article.readTime}</span>
          </div>
          <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
            {article.title}
          </h1>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
            {article.description}
          </p>
        </header>

        <div className="relative rounded-2xl overflow-hidden aspect-[16/7] mb-10 bg-background-100">
          <img
            src={article.heroImageUrl}
            alt={article.heroImageAlt}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Table of Contents */}
        <div className="bg-background-100 border border-background-200/70 rounded-xl p-5 mb-10">
          <h2 className="text-sm font-semibold text-foreground-900 mb-3">Table of Contents</h2>
          <nav className="flex flex-col gap-1.5">
            {article.tocItems.map((item) => (
              <a key={item.id} href={item.href} className="text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer">
                {item.label}
              </a>
            ))}
          </nav>
        </div>

        {/* Article Sections */}
        {article.sections.map((section, idx) => (
          <section key={section.id} id={section.id} className="mb-10">
            <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground-950 mb-4 leading-snug">
              {idx + 1}. {section.title}
            </h2>
            {section.paragraphs.map((p, pIdx) => (
              <p key={pIdx} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                {p}
              </p>
            ))}
            {section.imageUrl && (
              <div className="relative rounded-xl overflow-hidden aspect-[16/8] mb-4 bg-background-100">
                <img
                  src={section.imageUrl}
                  alt={section.imageAlt || section.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            )}
          </section>
        ))}

        {/* FAQ Section */}
        {article.faqItems.length > 0 && (
          <section className="mb-10">
            <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground-950 mb-6">
              Frequently Asked Questions
            </h2>
            <div className="space-y-3">
              {article.faqItems.map((faq) => (
                <details key={faq.id} className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
                  <summary className="flex items-center justify-between gap-3 px-5 py-4 cursor-pointer list-none">
                    <span className="text-sm font-medium text-foreground-800 pr-6">{faq.question}</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 w-5 h-5 flex items-center justify-center flex-shrink-0 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>
        )}

        {/* CTA */}
        <div className="bg-gradient-to-br from-accent-50/80 to-primary-50/50 border border-accent-200/50 rounded-2xl p-8 text-center">
          <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-2">
            Ready to Start Shopping Smarter?
          </h3>
          <p className="text-sm text-foreground-600 mb-5 max-w-md mx-auto">
            Head over to our main store and browse thousands of curated products organized by category.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="whitespace-nowrap inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-semibold px-7 py-3 rounded-full transition-all duration-300 hover:scale-105 cursor-pointer text-sm"
            >
              <i className="ri-shopping-bag-line w-5 h-5 flex items-center justify-center"></i>
              Visit kaspreadsheet.com
            </a>
            <a
              href="/"
              className="whitespace-nowrap inline-flex items-center gap-2 bg-secondary-500 hover:bg-secondary-600 text-white font-semibold px-7 py-3 rounded-full transition-all duration-300 hover:scale-105 cursor-pointer text-sm"
            >
              <i className="ri-home-line w-5 h-5 flex items-center justify-center"></i>
              Back to Home
            </a>
          </div>
        </div>

        {/* Internal Links */}
        <div className="mt-10 pt-8 border-t border-background-200/70">
          <h3 className="text-sm font-semibold text-foreground-900 mb-3">Explore More Resources</h3>
          <nav className="flex flex-wrap gap-2">
            <a
              href="/"
              className="whitespace-nowrap inline-flex items-center gap-1.5 text-xs font-medium text-foreground-600 bg-background-100 hover:bg-accent-100 hover:text-accent-700 rounded-full px-3 py-1.5 transition-all cursor-pointer"
            >
              <i className="ri-home-line w-3.5 h-3.5 flex items-center justify-center"></i>
              Superbuy Spreadsheet Home
            </a>
            {['how-to-use-superbuy-spreadsheet', 'is-superbuy-spreadsheet-safe', 'superbuy-spreadsheet-beginner-guide', 'superbuy-spreadsheet-review'].filter(s => s !== article.slug).slice(0, 3).map((s) => {
              const a = articleDataMap[s];
              return a ? (
                <a
                  key={s}
                  href={`/${s}/`}
                  className="whitespace-nowrap inline-flex items-center gap-1.5 text-xs font-medium text-foreground-600 bg-background-100 hover:bg-accent-100 hover:text-accent-700 rounded-full px-3 py-1.5 transition-all cursor-pointer"
                >
                  <i className="ri-article-line w-3.5 h-3.5 flex items-center justify-center"></i>
                  {a.title.length > 35 ? a.title.slice(0, 35) + '...' : a.title}
                </a>
              ) : null;
            })}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="whitespace-nowrap inline-flex items-center gap-1.5 text-xs font-medium text-foreground-600 bg-background-100 hover:bg-accent-100 hover:text-accent-700 rounded-full px-3 py-1.5 transition-all cursor-pointer"
            >
              <i className="ri-external-link-line w-3.5 h-3.5 flex items-center justify-center"></i>
              Main Store
            </a>
          </nav>
        </div>
      </article>

      {/* Footer */}
      <footer className="w-full bg-background-100 border-t border-background-200/70 py-8 px-4 md:px-6">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-foreground-500">
            &copy; 2026 superbuyspread.baby — All rights reserved.
          </p>
          <a href="/" className="text-xs text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer">
            Back to Homepage
          </a>
        </div>
      </footer>

      {/* Schema.org BlogPosting JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: article.title,
            description: article.description,
            image: article.heroImageUrl,
            datePublished: '2026-07-01',
            dateModified: article.updatedDate.replace('July ', '2026-07-').replace(' ', ''),
            author: {
              '@type': 'Organization',
              name: 'Superbuy Spreadsheet',
              url: 'https://superbuyspread.baby/',
            },
            publisher: {
              '@type': 'Organization',
              name: 'Superbuy Spreadsheet',
              url: 'https://superbuyspread.baby/',
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://superbuyspread.baby/${article.slug}/`,
            },
          }),
        }}
      />
    </main>
  );
}